import * as React from 'react';
import * as z from 'zod';
import { useLingui } from '@lingui/react/macro';
import { i18n } from '@lingui/core';
import type { PortalLocale } from '@worldcoo/monolith/portals';

const zodLocaleMap: Record<string, () => Promise<{ localeError: z.core.$ZodErrorMap }>> = {
	ca: () => import('zod/locales').then((m) => m.ca()),
	de: () => import('zod/locales').then((m) => m.de()),
	en: () => import('zod/locales').then((m) => m.en()),
	es: () => import('zod/locales').then((m) => m.es()),
	eu: () => import('zod/locales').then((m) => m.es()), // Basque: fallback to Spanish
	fr: () => import('zod/locales').then((m) => m.fr()),
	gl: () => import('zod/locales').then((m) => m.es()), // Galician: fallback to Spanish
	it: () => import('zod/locales').then((m) => m.it()),
	nl: () => import('zod/locales').then((m) => m.nl()),
	pt: () => import('zod/locales').then((m) => m.pt()),
};

export function useZodI18n() {
	const { t } = useLingui();
	const locale = (i18n.locale || 'en') as PortalLocale;

	React.useEffect(() => {
		const loadLocale = zodLocaleMap[locale] ?? zodLocaleMap['en']!;
		loadLocale().then((zodLocale) => {
			z.config({
				...zodLocale,
				customError: (issue) => {
					switch (issue.code) {
						case 'too_small': {
							if ((issue.origin === 'string' || issue.origin === 'array') && issue.minimum === 1) return t`Required`;
							const min = String(issue.minimum);
							if (issue.origin === 'string') return t`Minimum ${min} characters`;
							if (issue.origin === 'number' || issue.origin === 'int') return t`Minimum ${min}`;
							return undefined;
						}
						case 'too_big': {
							const max = String(issue.maximum);
							if (issue.origin === 'string') return t`Maximum ${max} characters`;
							if (issue.origin === 'number' || issue.origin === 'int') return t`Maximum ${max}`;
							return undefined;
						}
						case 'invalid_type': {
							if (issue.expected === 'string' || issue.expected === 'nonoptional' || issue.expected === 'number')
								return t`Required`;
							return undefined;
						}
						case 'invalid_format': {
							if (issue.format === 'email') return t`Invalid email`;
							if (issue.format === 'url') return t`Invalid URL`;
							if (issue.format === 'date' || issue.format === 'datetime') return t`Invalid date`;
							return undefined;
						}
						default:
							return undefined;
					}
				},
			});
		});
	}, [t, locale]);
}
